// variant-c.jsx — App shell. Bottom tab bar (Now / Hourly / 10 Day / Radar / More).
// Ported from /tmp/handoff-fcc-weather/src/variants-cd.jsx::VariantC.

import { useState } from 'preact/hooks';
import { WxIcon, Glyph } from '../icons.jsx';
import { HeroMedia } from '../hero/hero-media.jsx';
import { PullToRefresh, DayRow, MetricChip, SectionHead, Wordmark, AlertBanner, AuthFooter } from '../atoms.jsx';
import { RadarMap } from '../maps/radar-map.jsx';
import {
  getCurrentForState, getHourly, getTenDay, getMetrics,
  getAlert, hasActiveAlert, getLocation, getUpdatedLabel, getSun, getAlmanac,
  getPollen, getMoonRow, getFieldNotes, getQuote, getRawState,
  refresh,
} from '../data-bridge.js';

export function VariantC({ brand, density, state, sheetOpen, setSheetOpen, tipFn, heroTreatment = 'subtle', bucket = 'midday', brandKey = 'shared' }) {
  const [tab, setTab] = useState('now');
  const s = getCurrentForState(state);
  const hourly = getHourly(state);
  const tenDay = getTenDay(state);
  const metrics = getMetrics(state);
  const alert = getAlert();
  const sun = getSun();
  const location = getLocation();
  const updated = getUpdatedLabel();
  const almanac = getAlmanac();
  const pollen = getPollen();
  const moonRow = getMoonRow(1);
  const fieldNotes = getFieldNotes();
  const quote = getQuote();
  const unit = getRawState().unit;
  const max = Math.max(...tenDay.map((d) => d.hi));
  const min = Math.min(...tenDay.map((d) => d.lo));
  const [expanded, setExpanded] = useState(null);
  const showAlert = hasActiveAlert() || state === 'storm' || state === 'rain';

  const tabs = [
    { id: 'now',    label: 'Now',    icon: 'sun'   },
    { id: 'hourly', label: 'Hourly', icon: 'clock' },
    { id: '10d',    label: '10 Day', icon: 'cal'   },
    { id: 'radar',  label: 'Radar',  icon: 'rad'   },
    { id: 'more',   label: 'More',   icon: 'menu'  },
  ];

  const tabIcon = (k, color) => {
    const c = color;
    if (k === 'sun')   return <Glyph.Sun s={20} c={c}/>;
    if (k === 'clock') return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={c} strokeWidth="1.6"><circle cx="10" cy="10" r="7"/><path d="M10 6v4l3 2" strokeLinecap="round"/></svg>);
    if (k === 'cal')   return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={c} strokeWidth="1.6"><rect x="3" y="4" width="14" height="13" rx="2"/><path d="M3 8h14M7 2v4M13 2v4" strokeLinecap="round"/></svg>);
    if (k === 'rad')   return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={c} strokeWidth="1.6"><circle cx="10" cy="10" r="8"/><circle cx="10" cy="10" r="4"/><circle cx="10" cy="10" r="1" fill={c}/></svg>);
    if (k === 'menu')  return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><circle cx="5" cy="10" r="1.6" fill={c}/><circle cx="10" cy="10" r="1.6" fill={c}/><circle cx="15" cy="10" r="1.6" fill={c}/></svg>);
    return null;
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', color: brand.text }}>
      <PullToRefresh brand={brand} onRefresh={refresh}>
        <div style={{ paddingTop: 72, paddingBottom: 100 }}>
          {/* top: location + brand */}
          <div style={{ padding: `0 ${density.pad}px ${density.gap}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={() => setSheetOpen?.(true)} className="wx-tap" style={{ background: 'transparent', border: 'none', color: brand.text, display: 'flex', alignItems: 'center', gap: 6, padding: 0, cursor: 'pointer' }}>
              <Glyph.Pin s={14} c={brand.accent}/>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{location.label}</span>
              <Glyph.Chevron s={12} c={brand.textDim}/>
            </button>
            <Wordmark brand={brand} size={12}/>
          </div>

          {tab === 'now' && (
            <>
              {showAlert && <AlertBanner alert={alert} brand={brand} density={density} compact/>}
              <div style={{ position: 'relative', margin: `${density.gap}px ${density.pad}px ${density.gap}px`, borderRadius: 20, overflow: 'hidden', border: `1px solid ${brand.line}` }}>
                <div style={{ position: 'absolute', inset: 0, opacity: heroTreatment === 'punch' ? .8 : .45 }}>
                  <HeroMedia state={s.icon === 'partly-cloudy' ? 'cloudy' : s.icon} brand={brand}
                             treatment={heroTreatment === 'punch' ? 'punch' : 'subtle'} motion={heroTreatment !== 'poster'}
                             bucket={bucket} brandKey={brandKey}/>
                </div>
                <div style={{ position: 'relative', padding: `${density.gap + 4}px ${density.pad}px`, textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                    <WxIcon kind={s.icon} size={96}/>
                  </div>
                  <div style={{ fontSize: 88, fontWeight: 200, lineHeight: .9, letterSpacing: '-.04em', fontVariantNumeric: 'tabular-nums', color: '#fff', textShadow: '0 4px 16px rgba(0,0,0,.4)' }}>
                    {s.temp}°
                  </div>
                  <div style={{ fontSize: 16, marginTop: 8, color: '#fff' }}>{s.cond}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.75)', marginTop: 2 }}>H {s.hi}° · L {s.lo}° · Feels {s.feels}°</div>
                  <div style={{ marginTop: 10, fontSize: 10, color: 'rgba(255,255,255,.65)', fontFamily: brand.eyebrowFont }}>{updated}</div>
                </div>
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8,
                padding: `0 ${density.pad}px ${density.gap}px`,
              }}>
                {metrics.slice(0, 4).map((m) => <MetricChip key={m.k} m={m} brand={brand} density={density} onLongPress={() => tipFn?.({ tip: m.why, anchor: m.k })}/>)}
              </div>

              <div style={{ margin: `0 ${density.pad}px ${density.gap}px`, padding: '12px 14px', background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Glyph.Sun s={18} c={brand.accent}/>
                  <div>
                    <div style={{ fontSize: 13 }}>{sun.minsToSet > 0 ? `Sunset in ${Math.floor(sun.minsToSet / 60)}h ${sun.minsToSet % 60}m` : 'After sunset'}</div>
                    <div style={{ fontSize: 11, color: brand.textDim }}>{sun.set} · day length {sun.dayLen}</div>
                  </div>
                </div>
                <Glyph.Chevron s={14} c={brand.textDim} dir="right"/>
              </div>
            </>
          )}

          {tab === 'hourly' && (
            <>
              <SectionHead num="12 Hours" title="Hourly outlook" right={updated} brand={brand} density={density}/>
              <div style={{ padding: `0 ${density.pad}px` }}>
                {hourly.map((hr, i) => (
                  <div key={i} className="wx-tap" style={{
                    display: 'grid', gridTemplateColumns: '60px 36px 1fr 60px',
                    alignItems: 'center', gap: 10, padding: '10px 4px',
                    borderBottom: `1px solid ${brand.line}`,
                  }}>
                    <div style={{ fontSize: 13, color: i === 0 ? brand.text : brand.textDim, fontFamily: brand.eyebrowFont, fontWeight: i === 0 ? 700 : 400 }}>{hr.time}</div>
                    <WxIcon kind={hr.icon} size={26}/>
                    <div style={{ position: 'relative', height: 6, background: 'rgba(255,255,255,.06)', borderRadius: 3 }}>
                      <div style={{ position: 'absolute', inset: 0, width: `${hr.pop}%`, background: `linear-gradient(90deg, ${brand.accentDeep}, #60a5fa)`, borderRadius: 3 }}/>
                      {hr.pop > 5 && <span style={{ position: 'absolute', right: 6, top: -3, fontSize: 9, color: '#93c5fd' }}>{hr.pop}%</span>}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{hr.temp}°</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === '10d' && (
            <>
              <SectionHead num="The Outlook" title="10 Day" right="Tap to expand" brand={brand} density={density}/>
              {tenDay.map((row, i) => <DayRow key={i} row={row} idx={i} max={max} min={min} brand={brand} density={density} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)}/>)}
            </>
          )}

          {tab === 'radar' && (
            <>
              <SectionHead num="Radar" title="Precipitation" right="RainViewer" brand={brand} density={density}/>
              <div style={{ padding: `0 ${density.pad}px` }}>
                <RadarMap brand={brand}/>
              </div>
            </>
          )}

          {tab === 'more' && (
            <>
              <SectionHead title="More" brand={brand} density={density}/>
              <div style={{ padding: `0 ${density.pad}px` }}>
                {[
                  { k: 'UV index',          v: metrics[3]?.v ?? '—' },
                  { k: 'Wind & gusts',      v: metrics[2]?.v ?? '—' },
                  { k: 'Pollen forecast',   v: pollen && pollen.length ? pollen.slice(0, 2).map((p) => `${p.kind} ${p.tier.toLowerCase()}`).join(' · ') : 'No data' },
                  { k: 'Almanac & records', v: `${almanac.avgHi}° avg high` },
                  { k: 'Moon tonight',      v: moonRow[0] ? `${moonRow[0].illum}% ${moonRow[0].phase}` : '—' },
                  { k: 'Field notes',       v: fieldNotes.length ? `${fieldNotes.length} note${fieldNotes.length === 1 ? '' : 's'}` : 'None today' },
                  { k: 'Quote of the day',  v: quote ? `${quote.author} · ${quote.source}` : '—' },
                  { k: 'Sources',           v: 'Open-Meteo, NWS, RainViewer' },
                  { k: 'Settings',          v: `°${unit} · 12h · ${unit === 'C' ? 'Metric' : 'Imperial'}` },
                ].map((item) => (
                  <div key={item.k} className="wx-tap" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 4px', borderBottom: `1px solid ${brand.line}`,
                  }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500, color: brand.text }}>{item.k}</div>
                      <div style={{ fontSize: 12, color: brand.textDim, marginTop: 1 }}>{item.v}</div>
                    </div>
                    <Glyph.Chevron s={14} c={brand.border} dir="right"/>
                  </div>
                ))}
              </div>
              <AuthFooter brand={brand} density={density}/>
            </>
          )}
        </div>
      </PullToRefresh>

      {/* bottom tab bar */}
      <div style={{
        position: 'absolute', left: 8, right: 8, bottom: 28,
        background: brand.panel + 'd0', backdropFilter: 'blur(20px) saturate(160%)',
        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        border: `1px solid ${brand.line}`, borderRadius: 22,
        display: 'flex', padding: 6, gap: 2,
        boxShadow: '0 8px 24px rgba(0,0,0,.4)', zIndex: 50,
      }}>
        {tabs.map((t) => {
          const active = t.id === tab;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className="wx-tap" style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              padding: '8px 4px', borderRadius: 16, border: 'none', cursor: 'pointer',
              background: active ? brand.accent + '22' : 'transparent',
              color: active ? brand.accent : brand.textDim, transition: 'background .2s, color .2s',
            }}>
              {tabIcon(t.icon, active ? brand.accent : brand.textDim)}
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.02em' }}>{t.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
